import React, { useEffect, useState, useContext } from "react";
import PropTypes from "prop-types";
import { Col, Row } from "@pds-react/grid";
import Select, { createFilter } from "react-select";
import AsyncSelect from "react-select/async";
import Checkbox from "@pds-react/checkbox";
import Input from "@pds-react/input";
import Config from "../../../config";
import { fetchService } from "../../../services/fetchService";
import UserContext from "../../../context/User.context";
import { HandleSelectChange, HandleChange, HandleSameAsMultiFillCheckBox } from "./Helpers/FormHelpers";
import {
  Validate,
  ValidateRequired,
  ValidateRequiredSelect,
  validationUseEffectHelper,
} from "../../Utilities/ValidationHelpers";
import ValidationErrorMessage from "../../Utilities/ValidationErrorMessage";
import Loading from "../../Loading/Loading";
import { multiFetch } from "../../../services/multiFetch";
import HeaderAndButtons from "./Shared/HeaderAndButtons";
import { new_pa } from "../Data";
import selectStyler from "../../Utilities/selectStyler";
import { set_nulls_to_empty_strings } from "../../Utilities/FormHelpers";
import { useNavigate } from "react-router-dom";

const TradingAccountForm = ({ setForm, formData, navigation }) => {
  const {
    service_provider,
    service_provider_same_for_settlement,
    service_provider_for_settlement,
    service_provider_bin,
    service_provider_sub_acct,
    trading_account_relationship,
    fif,
    trading_account_name,
    trading_wire_instructions,
    wsrsStatus,
  } = formData;

  const { previous, next } = navigation;
  const { baseUrl } = Config.api;
  const { accessToken } = useContext(UserContext);
  const navigate = useNavigate();

  const [accountTypeOptions, setAccountTypeOptions] = useState();
  const [serviceProviderOptions, setServiceProviderOptions] = useState();
  const [accountDisabled, setAccountDisabled] = useState(false);
  const [omniInvisible, setOmniInvisible] = useState(true);
  const [subAccountDisabled, setSubAccountDisabled] = useState(false);
  const [loadingTradingNameData, setLoadingTradingNameData] = useState(true);

  const initialValidation = {
    trading_account_relationship: ValidateRequiredSelect,
    service_provider: ValidateRequiredSelect,
    service_provider_for_settlement: ValidateRequiredSelect,
  };

  const [validationErrors, setValidationError] = useState({});
  const [fieldsToValidate, setFieldsToValidate] = useState(initialValidation);

  const goToNextPageCheck = () => {
    let [formIsValid, errors] = Validate(formData, fieldsToValidate);
    setValidationError(errors);
    if (formIsValid) {
      next();
    }
  };

  const handleServiceChange = (id, value) => {
    if (value === null) {
      value = { value: "", label: "" };
    }
    setForm((formData) => ({
      ...formData,
      [id]: {
        label: value.label,
        value: value.value,
        ...value,
      },
    }));
  };

  const handleTradingNameChange = async (id, e) => {
    const newFormData = { ...formData };
    let value = set_nulls_to_empty_strings(e);
    if (e === null) {
      value = { value: "", label: "" };
      const formReset = JSON.parse(JSON.stringify(new_pa));
      newFormData.trading_account_name = formReset.trading_account_name;
      newFormData.trading_wire_instructions = formReset.trading_wire_instructions;
      newFormData.service_provider = formReset.service_provider;

      newFormData.fif = formReset.fif;
      newFormData.service_provider_same_for_settlement = formReset.service_provider_same_for_settlement;
      newFormData.service_provider_for_settlement = formReset.service_provider_for_settlement;
      newFormData.service_provider_bin = formReset.service_provider_bin;
    } else {
      setLoadingTradingNameData(true);
      const urls = [
        `${baseUrl}/v1/service-provider?service-provider-id=${value.service_provider_id}`,
        `${baseUrl}/v1/service-provider?service-provider-id=${value.settlement_service_provider_id}`,
      ];

      const parsedProviderTradingData = await multiFetch(urls, accessToken);

      const service_provider_response = parsedProviderTradingData[0].data[0];
      const service_provider_settlement_response = parsedProviderTradingData[1].data[0];

      newFormData.trading_account_name = { label: value.omnibus_name, value: value.trading_account_id, ...value };
      newFormData.service_provider_bin = value.bin_number;
      newFormData.service_provider = {
        label: service_provider_response.service_provider_name,
        value: service_provider_response.service_provider_id,
        trading_nscc_number: service_provider_response.trading_nscc_number,
        service_provider_name: service_provider_response.service_provider_name,
      };
      newFormData.service_provider_for_settlement = {
        label: service_provider_settlement_response.service_provider_name,
        value: service_provider_settlement_response.service_provider_id,
        trading_nscc_number: service_provider_settlement_response.trading_nscc_number,
        service_provider_name: service_provider_settlement_response.service_provider_name,
      };
      newFormData.trading_wire_instructions = value.wire_instructions;
      setLoadingTradingNameData(false);
    }
    newFormData[id] = value;
    setForm(newFormData);
  };

  const handleAccountRelationshipChange = (e) => {
    const newFormData = { ...formData };
    let value = e;
    if (e === null) {
      value = { value: "", label: "" };
    }
    newFormData.trading_account_relationship = value;
    if (!wsrsStatus && !service_provider_sub_acct) {
      const formReset = JSON.parse(JSON.stringify(new_pa));
      newFormData.trading_account_name = formReset.trading_account_name;
      newFormData.trading_wire_instructions = formReset.trading_wire_instructions;
      newFormData.service_provider = formReset.service_provider;
      newFormData.service_provider_sub_acct = formReset.service_provider_sub_acct;
      newFormData.service_provider_same_for_settlement = formReset.service_provider_same_for_settlement;
      newFormData.service_provider_for_settlement = formReset.service_provider_for_settlement;
      newFormData.service_provider_bin = formReset.service_provider_bin;
    }
    setForm(newFormData);

    let newValidationErrors = { ...validationErrors };
    let newFieldsToValidate = { ...fieldsToValidate };

    if (value.label.trim() === "Direct Traded Account") {
      setAccountDisabled(false);
      setOmniInvisible(true);
      delete newValidationErrors.service_provider_sub_acct;
      delete newValidationErrors.trading_account_name;
      newFieldsToValidate["service_provider"] = ValidateRequiredSelect;
      newFieldsToValidate["service_provider_for_settlement"] = ValidateRequiredSelect;

      delete newFieldsToValidate.service_provider_sub_acct;
      delete newFieldsToValidate.trading_account_name;
    } else {
      if (value.label.trim() === "Sub-Account") {
        setAccountDisabled(true);
        setOmniInvisible(false);

        delete newValidationErrors.service_provider;
        delete newValidationErrors.service_provider_for_settlement;
        newFieldsToValidate["service_provider_sub_acct"] = ValidateRequired;
        newFieldsToValidate["trading_account_name"] = ValidateRequiredSelect;
        delete newFieldsToValidate.service_provider;
        delete newFieldsToValidate.service_provider_for_settlement;
      } else {
        //RESET ALL VALUES
        setAccountDisabled(true);
        setOmniInvisible(true);
        delete newValidationErrors.service_provider_sub_acct;
        delete newValidationErrors.trading_account_name;
        delete newValidationErrors.service_provider;
        delete newValidationErrors.service_provider_for_settlement;

        delete newFieldsToValidate.service_provider_sub_acct;
        delete newFieldsToValidate.trading_account_name;
        delete newFieldsToValidate.service_provider;
        delete newFieldsToValidate.service_provider_for_settlement;
      }
    }
    setValidationError(newValidationErrors);
    setFieldsToValidate(newFieldsToValidate);
  };

  const buildAccountTypeOptions = (options) => {
    return options.map((opt) => {
      if (opt.account_type === "Direct") {
        return { value: opt.account_type_id, label: "Direct Traded Account" };
      } else {
        return { value: opt.account_type_id, label: "Sub-Account" };
      }
    });
  };

  const fetchAccountTypes = async (endpoint) => {
    try {
      let response = await fetchService(baseUrl + endpoint, { method: "GET", token: accessToken });
      let resp = await response.json();
      if (200 !== response.status) {
        throw new Error(resp.requestId);
      }
      if (!trading_account_relationship.value) {
        if (wsrsStatus && service_provider_sub_acct) {
          resp.data.forEach((result) => {
            if (result.account_type === "Omnibus") {
              handleAccountRelationshipChange({ label: "Sub-Account", value: result.account_type_id });
            }
            setSubAccountDisabled(true);
          });
        } else {
          resp.data.forEach((result) => {
            if (result.account_type === "Direct") {
              HandleSelectChange(
                "trading_account_relationship",
                { label: "Direct Traded Account", value: result.account_type_id },
                formData,
                setForm
              );
            }
          });
        }
      }

      setAccountTypeOptions(buildAccountTypeOptions(resp.data));
      setLoadingTradingNameData(false);
    } catch (error) {
      navigate("/error", { state: { error } });
    }
  };

  const buildAccountNameOptions = (options) => {
    let ops = [];
    options.map((opt) => {
      if (opt.omnibus_name !== null)
        ops.push({
          value: opt.trading_account_id,
          label: opt.transfer_agent_account_number
            ? `[${opt.transfer_agent_account_number}] -- ${opt.omnibus_name}`
            : opt.omnibus_name,
          omnibus_name: opt.omnibus_name,
          transfer_agent_account_number: opt.transfer_agent_account_number ? opt.transfer_agent_account_number : "",
          ...opt,
        });
    });
    return ops;
  };

  const loadAccountNameOptions = async (input, callback) => {
    let endpoint = `/v1/trading-account-name-or-number?name-or-number=${input}`;

    if (input.length > 2) {
      try {
        let response = await fetchService(baseUrl + endpoint, { method: "GET", token: accessToken });
        let resp = await response.json();
        if (200 !== response.status) {
          throw new Error(resp.requestId);
        }
        return callback(buildAccountNameOptions(resp.data));
      } catch (error) {
        navigate("/error", { state: { error } });
      }
    }
  };

  const getserviceProviderData = async () => {
    try {
      let endpoint = "/v1/service-provider";
      let response = await fetchService(baseUrl + endpoint, { method: "GET", token: accessToken });
      let resp = await response.json();
      if (200 !== response.status) {
        const requestId = resp["requestId"];
        throw new Error(requestId);
      }
      let res = await resp["data"];
      setServiceProviderOptions(
        res.map((result) => {
          return {
            label: result.trading_nscc_number
              ? `[${result.trading_nscc_number}] -- ${result.service_provider_name}`
              : result.service_provider_name,
            value: result.service_provider_id,
            trading_nscc_number: result.trading_nscc_number,
            service_provider_name: result.service_provider_name,
          };
        })
      );
    } catch (error) {
      navigate("/error", { state: { error } });
    }
  };

  const formatSpLabel = ({ trading_nscc_number, service_provider_name }) => (
    <div>
      <p style={{ marginBottom: 0 }}>
        <span style={{ color: "#0076CF" }}>{trading_nscc_number}</span> |{" "}
        <span style={{ color: "#00C4D9" }}>{service_provider_name}</span>
      </p>
    </div>
  );

  const formatTaLabel = ({ transfer_agent_account_number, omnibus_name }) => (
    <div>
      <p style={{ marginBottom: 0 }}>
        <span style={{ color: "#0076CF" }}>{transfer_agent_account_number}</span> |{" "}
        <span style={{ color: "#00C4D9" }}>{omnibus_name}</span>
      </p>
    </div>
  );

  useEffect(() => {
    fetchAccountTypes("/v1/account-type");
  }, []);

  useEffect(() => {
    getserviceProviderData();
  }, []);

  useEffect(() => {
    validationUseEffectHelper(
      "trading_account_relationship",
      trading_account_relationship,
      fieldsToValidate,
      validationErrors,
      setValidationError
    );
  }, [trading_account_relationship]);

  useEffect(() => {
    validationUseEffectHelper(
      "service_provider_for_settlement",
      service_provider_for_settlement,
      fieldsToValidate,
      validationErrors,
      setValidationError
    );
  }, [service_provider_for_settlement]);

  useEffect(() => {
    validationUseEffectHelper(
      "service_provider",
      service_provider,
      fieldsToValidate,
      validationErrors,
      setValidationError
    );

    if (service_provider_same_for_settlement) {
      setForm((formData) => ({ ...formData, service_provider_for_settlement: service_provider }));
    }
  }, [service_provider]);

  useEffect(() => {
    validationUseEffectHelper(
      "trading_account_name",
      trading_account_name,
      fieldsToValidate,
      validationErrors,
      setValidationError
    );
  }, [trading_account_name]);

  useEffect(() => {
    validationUseEffectHelper(
      "service_provider_sub_acct",
      service_provider_sub_acct,
      fieldsToValidate,
      validationErrors,
      setValidationError
    );
  }, [service_provider_sub_acct]);

  useEffect(() => {
    if (trading_account_relationship.label === "Sub-Account") {
      setAccountDisabled(true);
      setOmniInvisible(false);
      if (wsrsStatus && service_provider_sub_acct) {
        setSubAccountDisabled(true);
      }
    }
  }, []);

  if (!loadingTradingNameData) {
    return (
      <form data-testid={"tradingAccount"}>
        <HeaderAndButtons
          prev_label={"Fiduciary"}
          prev_funct={previous}
          next_label={"Investments"}
          next_funct={goToNextPageCheck}
          header_label={"Trading Account"}
        />
        <Row>
          <Col sm={6} lg={6} className="util-margin-top-sm-15 util-margin-top-xs-30">
            <label>Trading Account Relationship</label>
            <div data-testid={"trading_account_relationship"}>
              <Select
                isClearable={true}
                datatestid={`trading_account_relationship`}
                id={`trading_account_relationship`}
                inputId={`trading_account_relationship_input`}
                styles={selectStyler({
                  validationErrorsValue: validationErrors["trading_account_relationship"],
                  disabled: subAccountDisabled,
                })}
                isDisabled={subAccountDisabled}
                options={accountTypeOptions}
                value={trading_account_relationship["label"] !== "" ? trading_account_relationship : "undefined"}
                onChange={(e) => {
                  handleAccountRelationshipChange(e);
                }}
              ></Select>
              <ValidationErrorMessage
                paramId="trading_account_relationship"
                validationError={validationErrors["trading_account_relationship"]}
              />
            </div>
          </Col>
          {omniInvisible ? (
            <></>
          ) : (
            <Col
              sm={6}
              lg={6}
              className="util-margin-top-sm-15 util-margin-top-xs-30"
              data-testid={`trading_account_name`}
            >
              <label>Trading Account Name</label>
              <p>
                <span>Key: </span>
                <span style={{ color: "#0076CF" }}>Trading Account ID</span> |{" "}
                <span style={{ color: "#00C4D9" }}>Trading Account Name</span>
              </p>
              <AsyncSelect
                isClearable={true}
                id={`trading_account_name`}
                inputId={`trading_account_name_input`}
                formatOptionLabel={formatTaLabel}
                loadOptions={loadAccountNameOptions}
                styles={selectStyler({
                  validationErrorsValue: validationErrors["trading_account_name"],
                })}
                value={trading_account_name["label"] !== "" ? trading_account_name : "undefined"}
                onChange={(e) => {
                  handleTradingNameChange("trading_account_name", e);
                }}
              ></AsyncSelect>
              <ValidationErrorMessage
                paramId="trading_account_name"
                validationError={validationErrors["trading_account_name"]}
              />
            </Col>
          )}
        </Row>
        <Row>
          <Col sm={6} lg={6} className="util-margin-top-sm-15 util-margin-top-xs-30">
            <Input
              data-testid={"service_provider_bin"}
              id={"service_provider_bin"}
              type="text"
              label="Bank Identification Number (BIN)"
              value={service_provider_bin}
              onChange={(e) => HandleChange(e, "service_provider_bin", formData, setForm)}
              disabled={accountDisabled}
            ></Input>
          </Col>
          {omniInvisible ? (
            <></>
          ) : (
            <Col sm={6} lg={6} className="util-margin-top-sm-15 util-margin-top-xs-30">
              <Input
                data-testid={`sub_account_number`}
                id={`sub_account_number`}
                type="text"
                label="Service Provider Sub Account #"
                value={service_provider_sub_acct}
                onChange={(e) => HandleChange(e, "service_provider_sub_acct", formData, setForm)}
                errorMessage={validationErrors["service_provider_sub_acct"]}
                disabled={omniInvisible || subAccountDisabled}
              ></Input>
            </Col>
          )}
        </Row>
        <Row>
          <Col sm={6} lg={6} className="util-margin-top-sm-15 util-margin-top-xs-30">
            <Checkbox
              data-testid={`fif`}
              id={`fif`}
              label="Fund in/of Funds (0bps) Trading Account"
              className="util-margin-top-sm-15 util-margin-top-xs-30"
              value={trading_account_relationship === "Direct Traded Account" ? fif : false}
              disabled={true}
              errorMessage={""}
            ></Checkbox>
          </Col>
        </Row>
        <Row>
          <Col sm={6} lg={6} className="util-margin-top-sm-15 util-margin-top-xs-30" data-testid={"service_provider"}>
            <label>Service Provider</label>
            <p>
              <span>Key: </span>
              <span style={{ color: "#0076CF" }}>NSCC Number</span> |{" "}
              <span style={{ color: "#00C4D9" }}>Service Provider Name</span>
            </p>
            <Select
              data-testid={"service_provider"}
              id={"service_provider"}
              formatOptionLabel={formatSpLabel}
              options={serviceProviderOptions}
              value={service_provider["label"] !== "" ? service_provider : "undefined"}
              onChange={(e) => handleServiceChange("service_provider", e, formData, setForm)}
              isClearable={true}
              styles={selectStyler({
                validationErrorsValue: validationErrors["service_provider"],
                disabled: accountDisabled,
                formattedLabel: true,
              })}
              isDisabled={accountDisabled}
            ></Select>
            <ValidationErrorMessage paramId="service_provider" validationError={validationErrors["service_provider"]} />
          </Col>
        </Row>
        <Row>
          <Col sm={6} lg={6} className="util-margin-top-sm-15 util-margin-top-xs-30">
            <Checkbox
              data-testid={"service_provider_same_for_settlement"}
              id={`service_provider_same_for_settlement`}
              label="Service Provider is the same for Settlement"
              className="util-margin-top-sm-15 util-margin-top-xs-30"
              checked={service_provider_same_for_settlement}
              onChange={(e) => {
                HandleSameAsMultiFillCheckBox(
                  "service_provider_same_for_settlement",
                  e.target.checked,
                  { service_provider: "service_provider_for_settlement" },
                  formData,
                  setForm
                );
              }}
              disabled={accountDisabled}
              errorMessage={""}
            ></Checkbox>
          </Col>
        </Row>
        <Row>
          <Col
            sm={6}
            lg={6}
            className="util-margin-top-sm-15 util-margin-top-xs-30"
            data-testid={"service_provider_for_settlement"}
          >
            <label>Service Provider for Settlement</label>
            <p>
              <span>Key: </span>
              <span style={{ color: "#0076CF" }}>NSCC Number</span> |{" "}
              <span style={{ color: "#00C4D9" }}>Service Provider Name</span>
            </p>
            <Select
              id={"service_provider_for_settlement"}
              options={serviceProviderOptions}
              formatOptionLabel={formatSpLabel}
              value={service_provider_for_settlement["label"] !== "" ? service_provider_for_settlement : "undefined"}
              onChange={(e) => handleServiceChange("service_provider_for_settlement", e, formData, setForm)}
              isDisabled={service_provider_same_for_settlement || accountDisabled}
              isClearable={true}
              filterOption={createFilter({ ignoreAccents: false })}
              styles={selectStyler({
                validationErrorsValue: validationErrors["service_provider_for_settlement"],
                disabled: service_provider_same_for_settlement || accountDisabled,
                formattedLabel: true,
              })}
            ></Select>
            <ValidationErrorMessage
              paramId="service_provider_for_settlement"
              validationError={validationErrors["service_provider_for_settlement"]}
            />
          </Col>
        </Row>
        <Row>
          <Col sm={12} lg={6}>
            <div className="util-margin-top-sm-15 util-margin-top-xs-30">
              <label htmlFor={`wire_instructions`}>Wire Instructions</label>
            </div>
            <div className="util-margin-top-sm-15 util-margin-top-xs-30">
              <textarea
                data-testid={`wire_instructions`}
                id={`wire_instructions`}
                value={trading_wire_instructions}
                style={{
                  resize: "none",
                  height: 200,
                  width: "-webkit-fill-available",
                  color: accountDisabled ? "#C7C7C7" : "none",
                }}
                onChange={(e) => HandleChange(e, "trading_wire_instructions", formData, setForm)}
                disabled={accountDisabled}
              ></textarea>
            </div>
          </Col>
        </Row>
      </form>
    );
  } else {
    return <Loading />;
  }
};

TradingAccountForm.propTypes = {
  setForm: PropTypes.func,
  formData: PropTypes.object,
  navigation: PropTypes.object,
};

export default TradingAccountForm;
